import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid, ResponsiveGridComponentProps } from '@adobe/aem-react-editable-components';
import { respGridUtil, transformTargetModel } from '@marriott/mi-headless-utils';

import { IDCADecisionContainerProps, IUXL_RESPONSE } from './DCADecisionContainer.types';
import { StyledDCADecisionContainer } from './DCADecisionContainer.styles';
import { updateCarousel } from '../../utils/helpers';

const riottMediaMapping = [
  'mi-aem-common-spa/components/content/riottmediacard',
  'mi-aem-common-spa/components/content/mangopilot', // this is old mapping it should be removed later
];

export const DCADecision: FC<IDCADecisionContainerProps> = props => {
  const {
    isAuthorMode,
    styleclass = '',
    pagePath,
    itemPath,
    cqItemsOrder = [],
    cqItems = {},
    mboxParameter = 'Empty-mbox',
    // when there is mbox authored then uxlResponse should be updated with that fallback response
    // and mboxParameter also have some fallback value to update carousel or component properly
    uxlResponse = mboxParameter === 'Empty-mbox'
      ? { isTargetContentLoaded: true, isTargetContentError: true }
      : undefined,
    displayEvergreenContent,
    isHide = false,
  } = props;
  const [isDataLoaded, setIsDataLoaded] = useState(isAuthorMode);
  const [childProps, setChildProps] = useState();
  const [containerStyleClass] = useState(styleclass);
  const ref = useRef<HTMLDivElement>(null);

  const updatedModelJson = (isMango: boolean, uxlResponse?: IUXL_RESPONSE) => {
    const hasAssets = !!(
      isMango &&
      Array.isArray(uxlResponse?.adData) &&
      uxlResponse?.adData[0]?.assets &&
      (uxlResponse?.adData[0]?.assets?.primaryImage_desktop ||
        uxlResponse?.adData[0]?.assets?.primaryImage_mobile ||
        uxlResponse?.adData[0]?.assets?.primaryImage_tablet ||
        uxlResponse?.adData[0]?.assets?.ctaURL)
    );
    let compToBeDisplayed = '',
      compJson = {};
    cqItemsOrder?.map((cardName: string) => {
      if (Object?.prototype?.hasOwnProperty?.call(cqItems, cardName)) {
        const card = cqItems?.[cardName] as Record<string, unknown>;
        const gridUtil = respGridUtil({ ...card }) || {};

        gridUtil?.cqItems &&
          Object.keys(gridUtil?.cqItems)?.forEach(keyname => {
            const isRiottMediaMapping = riottMediaMapping?.includes(gridUtil?.cqItems?.[keyname]?.[':type']);

            if (!uxlResponse && !displayEvergreenContent) {
              compToBeDisplayed = '';
            } else if ((isMango && hasAssets && isRiottMediaMapping) || (!isRiottMediaMapping && !isMango)) {
              compToBeDisplayed = keyname;
              compJson = {
                mboxParameter,
                ...transformTargetModel(gridUtil?.cqItems?.[keyname], uxlResponse ?? {}),
                isContainerComponent: true,
              };
            } else if (isMango && isRiottMediaMapping && !hasAssets) {
              const dcaKey =
                Object.keys(gridUtil?.cqItems).find(
                  key => !riottMediaMapping?.includes(gridUtil?.cqItems[key][':type'])
                ) || '';
              if (displayEvergreenContent) {
                compToBeDisplayed = dcaKey;
                compJson = {
                  ...gridUtil?.cqItems?.[dcaKey],
                  isContainerComponent: true,
                  isTargetContentLoaded: true,
                };
              } else {
                compToBeDisplayed = '';
              }
            }
          });
        if (compToBeDisplayed) {
          const updatedGridUtil = {
            ...gridUtil,
            cqItems: {
              [compToBeDisplayed]: { ...compJson },
            },
            cqItemsOrder: [compToBeDisplayed],
          };
          setChildProps(updatedGridUtil);
        } else {
          setChildProps(undefined);
        }
        return;
      }
    });
  };

  useEffect(() => {
    if (!uxlResponse && mboxParameter) return;

    if (uxlResponse && String(uxlResponse?.isTargetContentError) === 'false') {
      // means got successfull uxl response
      updatedModelJson(!!uxlResponse?.adData, uxlResponse);
    } else {
      // error in uxl response
      updatedModelJson(false, undefined);
    }
    setIsDataLoaded(true);
  }, [uxlResponse?.isTargetContentError, mboxParameter]);

  useEffect(() => {
    if (uxlResponse && mboxParameter && isDataLoaded) {
      if (uxlResponse?.isTargetContentError && String(displayEvergreenContent) !== 'true') {
        updateCarousel(ref, false); // means will be removed from carousel;
      } else updateCarousel(ref);
    }
  }, [uxlResponse?.isTargetContentLoaded, isDataLoaded]);

  const AuthorComponents = () => {
    return (
      <>
        {Array.from({ length: 1 }, (_, index) => (
          <div>
            <ResponsiveGrid
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              pagePath={pagePath}
              itemPath={`${itemPath}/${cqItemsOrder[index]}`}
              columnCount="12"
              gridClassNames={''}
              config={{
                isEmpty: () => true,
                resourceType: 'mi-aem-common-spa/components/container',
              }}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <StyledDCADecisionContainer
      data-testid="merch-DCADecisionContainer"
      data-component-name="o-merch-DCADecisionContainer"
      className={clsx(
        'dca-decision-container',
        containerStyleClass,
        containerStyleClass?.includes('fullbleed') ? 'm-container-fullbleed' : 'container px-0',
        { 'd-none ': isHide || (isDataLoaded && !childProps && !isAuthorMode) }
      )}
      ref={ref}
      data-experiment-name={uxlResponse?.['responseTokens']?.['activity.name']}
      data-variant={uxlResponse?.['responseTokens']?.['option.name']}
    >
      {isAuthorMode && <AuthorComponents />}
      {!isAuthorMode && (
        <div
          data-variation={
            childProps
              ? checkMobileRenditionForRiottMedia(getVariation(childProps), uxlResponse)
              : checkMobileRenditionForRiottMedia('riott-media', uxlResponse)
          }
          className={clsx('dca-decision-content', { 'skeleton-loader-composite py-4': !isDataLoaded })}
        >
          {isDataLoaded && childProps && (
            <ResponsiveGrid {...(childProps as unknown as ResponsiveGridComponentProps)} />
          )}
        </div>
      )}
    </StyledDCADecisionContainer>
  );
};

const Config = {
  emptyLabel: 'DCA Decision Container',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/dcadecisioncontainer`,
};

export const DCADecisionContainer: FC<IDCADecisionContainerProps> = props => (
  <EditableComponent config={Config} {...props}>
    <DCADecision {...props} />
  </EditableComponent>
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getVariation = (props: Record<string, any>) => {
  if (props?.['cqItems']) {
    const componentName = Object.keys(props?.['cqItems'])?.[0];
    const componentProps = { ...(props?.['cqItems']?.[componentName] ?? {}) };
    const componentType = props?.['cqItems']?.[componentName]?.[':type'] as string;
    switch (componentType) {
      case 'mi-aem-common-spa/components/content/riottmediacard':
        return 'riott-media';
      case 'mi-aem-common-spa/components/content/merchandisingsmallbanner':
        return componentProps?.['variation'];
      case 'mi-aem-common-spa/components/content/tilesquare':
        return componentProps?.['tileSquareVariation'];
      case 'mi-aem-common-spa/components/content/partnercard':
        return 'partner-card';
      default:
        return 'riott-media';
    }
  }
  return 'riott-media';
};

const checkMobileRenditionForRiottMedia = (componentType: string, uxlResponse: IUXL_RESPONSE | undefined) => {
  if (componentType === 'riott-media' && !uxlResponse?.adData?.[0]?.assets?.primaryImage_mobile) {
    return 'riott-media-non-mobile';
  } else {
    return componentType;
  }
};
